import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import clsx from 'clsx';
import { COUNTRIES, Country, findCountryByCode } from '@/data/countries';

interface Props {
  /** ISO-2 country code, e.g. 'AE'. */
  countryCode?: string;
  /** Local digits without dial code. */
  number: string;
  defaultCode?: string;
  onChange: (next: { countryCode: string; number: string; e164: string }) => void;
  placeholder?: string;
  invalid?: boolean;
  disabled?: boolean;
}

/**
 * CountryPhoneInput renders a dial-code dropdown next to a number field and
 * emits the assembled E.164 string. Useful for "Call" template buttons.
 */
export const CountryPhoneInput: React.FC<Props> = ({
  countryCode, number, defaultCode = 'AE', onChange, placeholder, invalid, disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const country = useMemo<Country>(() => findCountryByCode(countryCode) ?? findCountryByCode(defaultCode) ?? COUNTRIES[0], [countryCode, defaultCode]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return COUNTRIES;
    return COUNTRIES.filter((c) =>
      c.name.toLowerCase().includes(s) || c.code.toLowerCase().includes(s) || c.dial.includes(s)
    );
  }, [q]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 0);
    else setQ('');
  }, [open]);

  const emit = (cc: string, num: string) => {
    const c = findCountryByCode(cc) ?? country;
    const digits = num.replace(/\D/g, '');
    onChange({ countryCode: c.code, number: digits, e164: digits ? `${c.dial}${digits}` : '' });
  };

  const onPickCountry = (c: Country) => {
    setOpen(false);
    emit(c.code, number);
  };

  return (
    <div ref={containerRef} className={clsx('relative flex w-full items-stretch', disabled && 'opacity-60')}>
      {/* Dial code */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={clsx(
          'flex shrink-0 items-center gap-1.5 rounded-l-lg border border-r-0 px-3 text-sm font-medium transition-colors',
          'hover:bg-surface-alt focus:outline-none focus:ring-4 focus:ring-primary-soft focus:border-primary focus:z-10',
          invalid ? 'border-danger' : 'border-border',
          'bg-surface',
        )}
      >
        <span className="text-base leading-none">{country.flag}</span>
        <span className="font-mono text-text-secondary">{country.dial}</span>
        <ChevronDown size={13} className={clsx('text-text-muted transition-transform', open && 'rotate-180')} />
      </button>

      {/* Number */}
      <input
        type="tel"
        value={number}
        disabled={disabled}
        placeholder={placeholder ?? 'phone number'}
        onChange={(e) => emit(country.code, e.target.value)}
        className={clsx(
          'h-10 min-w-0 flex-1 rounded-r-lg border bg-surface px-3 text-sm text-text-primary placeholder:text-text-muted',
          'transition-colors',
          'hover:border-border-strong',
          'focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary-soft',
          invalid ? 'border-danger focus:border-danger focus:ring-danger-soft' : 'border-border',
        )}
      />

      {open && (
        <div className="absolute left-0 top-full z-30 mt-1 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-border bg-surface shadow-elevated animate-fade-in">
          <div className="border-b border-border p-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
              <input
                ref={searchRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search country..."
                className="h-9 w-full rounded-lg border border-border bg-surface-alt pl-9 pr-3 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary-soft"
              />
            </div>
          </div>
          <ul className="max-h-72 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <li className="px-3 py-3 text-center text-xs text-text-muted">No matches</li>
            )}
            {filtered.map((c) => {
              const active = c.code === country.code;
              return (
                <li key={c.code}>
                  <button
                    type="button"
                    onClick={() => onPickCountry(c)}
                    className={clsx(
                      'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors',
                      active ? 'bg-primary-soft text-primary' : 'text-text-primary hover:bg-surface-alt',
                    )}
                  >
                    <span className="text-base leading-none">{c.flag}</span>
                    <span className="flex-1 truncate">{c.name}</span>
                    <span className="font-mono text-xs text-text-muted">{c.dial}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
